import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { WebPage as entity } from '../../graphql/entities';
import createForm from '../../components/connectedForm/ConnectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import { default as Form } from '../../components/connectedForm';
import {
  IWebPage,
  IStaticBoxQueryResult,
  IStaticBoxesWorkspaceQueryVariables,
  FilterOperation,
  IWebPageEdge,
} from '../../graphql/schema/p4types';
import { IDropdownProps } from '../../@types/dropdown';
import { useDispatch, useSelector } from 'react-redux';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { IDateFieldProps } from '../../components/connectedForm/DateInput/DateInput';
import mapStateToFormProps from '../../utils/mapStateToFormProps';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import { EntityType } from '../../@types/entityTypes';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Wysiwyg } from '@ogl/lexical-editor';
import WebMediaFolderTreespace from './WebMediaFolderTreespace';
import { useCompany } from '../../utils/FetchConfig';
import StaticBoxWorkspaceQuery from '../../graphql/queries/web/StaticBoxWorkspaceQuery.graphql';
import { cloneDeep } from '@apollo/client/utilities';
import _debounce from 'lodash/debounce';
import WebPageMergedContent from '../../graphql/queries/web/WebPageMergedContent.graphql';

export const useStaticBlocks = () => {
  const [staticBlocks, setStaticBlocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filters = [
    {
      filterParameters: {
        field: 'title',
        value: searchTerm,
      },
      filterOperation: FilterOperation.Contains,
    },
    {
      filterParameters: {
        field: 'enabled',
        value: 'true',
      },
      filterOperation: FilterOperation.Equals,
    },
  ];

  const { data, refetch } = useQuery<IStaticBoxQueryResult, IStaticBoxesWorkspaceQueryVariables>(
    StaticBoxWorkspaceQuery,
    {
      variables: {
        first: 40,
        filters: filters,
        includeContent: true,
      },
    }
  );

  const [getMergedContent] = useLazyQuery<string, { value: string }>(WebPageMergedContent);

  useEffect(() => {
    if (data?.listData?.edges) {
      const nodesWithoutParent = data.listData.edges.map((edge: IWebPageEdge) => ({
        ...edge.node,
      }));
      setStaticBlocks(nodesWithoutParent);
    }
  }, [data?.listData?.edges]);

  const debouncedSearch = useCallback(
    _debounce((value: string) => {
      if (value === searchTerm) {
        refetch();
      } else {
        setSearchTerm(value);
      }
    }, 300),
    []
  );

  const onMergedContentRequested = async (unmergedContent: string) => {
    return await getMergedContent({ variables: { value: unmergedContent } });
  };

  return { staticBlocks, debouncedSearch, onMergedContentRequested };
};

export const useHandleEditorSubmit = (
  ref: React.MutableRefObject<any>,
  connectedForm: { change: (arg0: string, arg1: any) => any },
  editorFieldName: string
) => {
  const { submit } = connectedForm;
  const dispatch = useDispatch();

  return async () => {
    if (ref?.current?.getEditorValue) {
      const editorValue = await ref.current.getEditorValue();
      dispatch(connectedForm.change(editorFieldName, editorValue));
    }
    submit?.();
  };
};

const WebPageForm = ({ values }: { values: IWebPage }) => {
  const company = useCompany();
  const connectedForm = useContext(ConnectedFormContext);
  const webPage: IWebPage = values;
  const wysiwygRef = useRef();

  const { staticBlocks, debouncedSearch, onMergedContentRequested } = useStaticBlocks();
  const onSubmit = useHandleEditorSubmit(wysiwygRef, connectedForm, 'content');

  return (
    <Form.FormContainer>
      <Form.Header entityHdr="title" component={Form.HeaderBlock} archive={true} onSubmit={onSubmit} />
      <Form.Form>
        <Form.Row>
          <Form.Fieldset cols={1}>
            <Form.Legend label="General" />
            <Form.Field dataid="title" component={input.BlurTextField} label="Title" autoFocus={true} />
            <Form.Field dataid="url" component={input.BlurTextField} label="URL" />
            <Form.Field<IDropdownProps>
              dataid="parent"
              component={input.Dropdown}
              label="Parent Page"
              filterOption={(option) => {
                // exclude self from options as parent.
                if (typeof values.id !== 'undefined') {
                  if (values.id === option.id) {
                    return false;
                  }
                }
                return true;
              }}
            />
            <Form.Field dataid="metaDescription" component={input.BlurTextField} label="Meta Description" />
            <Form.Field dataid="searchKeywords" component={input.BlurTextField} label="Search Keywords" />
            <Form.Field<IDateFieldProps> dataid="publishedDate" component={input.DateField} label="Published Date" />
            <Form.Field dataid="enabled" component={input.Checkbox} label="Enabled" />
            <Form.Row>
              <Form.Fieldset cols={1}>
                <Form.Legend label="Content" />
                <Wysiwyg
                  ref={wysiwygRef}
                  value={webPage.content ?? ''}
                  customFileSearchComponent={WebMediaFolderTreespace}
                  customStyling={company?.wysiwygEditorCss}
                  staticBlocks={staticBlocks}
                  onRemoteBlockSearch={debouncedSearch}
                  onMergedContentRequested={onMergedContentRequested}
                />
              </Form.Fieldset>
            </Form.Row>
          </Form.Fieldset>
        </Form.Row>
      </Form.Form>
    </Form.FormContainer>
  );
};

const ConnectedWebPageForm: any = createForm({
  form: entity.type,
  name: entity.formTitle,
  entity: entity,
  queryFragment: entity.fragment,
  defaultReturnUrl: entity.workspaceUrl,
  forceUpdate: true,
  getSubmitValues: ({ values }: { values }) => {
    const webPage: IWebPage = cloneDeep(values);

    if (webPage.hasOwnProperty('mergedContent')) {
      delete webPage.mergedContent;
    }

    if (webPage?.parent?.hasOwnProperty('mergedContent')) {
      delete webPage.parent.mergedContent;
    }

    return webPage;
  },
})(WebPageForm);

const { default: defaultQuery, temporary, update, instance } = entity;

export default ({ temporaryDefault, overrideParams, ...props }) => {
  const formProps = useSelector((store) => mapStateToFormProps(store, props));
  const formDefault = useFormQuery(
    (temporaryDefault
      ? { default: temporary, instance: instance }
      : { default: defaultQuery, instance: instance }) as EntityType,
    temporaryDefault ? { forceDefault: true, idParam: 'uuid', overrideParams } : { duplicate: true, overrideParams }
  );

  const [formUpdate] = useMutation(update);

  return (
    <ConnectedWebPageForm
      {...formProps}
      {...props}
      formData={formDefault}
      formUpdate={formUpdate}
      overrideParams={overrideParams}
    />
  );
};
